/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { Box, Container, Link, Typography, styled } from '@mui/material'
import Logo from 'assets/icons/ClientLogo.svg'
import BackgroundImage from 'assets/images/watermark.png'
import { MButton } from 'components'
import { LoginBanner } from 'components/ui/Alert/LoginBanner'
import { SkipLink } from 'components/ui/SkipLink'
import { FOOTER_SOCIAL_LINKS, ROUTES_PATH, UNAUTH_TOPBAR_ITEMS } from 'config'
import { color } from 'config/color'
import type React from 'react'
import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'

interface IAuth {
  title?: string
  desc?: string
  subTitle?: string
  children: React.ReactNode
  isAuthPage?: boolean
}

export const AuthLayout = ({
  title,
  desc,
  isAuthPage = false,
  children
}: IAuth): JSX.Element => {
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useMobileBreakpoints()

  const authTitle = useMemo(() => {
    const pageData = UNAUTH_TOPBAR_ITEMS.find(
      (item) => item.href === location.pathname
    )

    return pageData?.title
  }, [location.pathname])

  const isFullScreen = useMemo(() => {
    if (
      location.pathname === ROUTES_PATH.SIGNUP_SUCCESS ||
      location.pathname === ROUTES_PATH.FORGOT_PASSWORD ||
      location.pathname === ROUTES_PATH.PAY
    ) {
      return true
    }

    return false
  }, [location.pathname])

  return (
    <>
      <SkipLink targetId="main-content">Skip to main content</SkipLink>
      <Wrapper isFullScreen={isFullScreen} isMobile={isMobile}>
        <PageHeader role="banner">
          <LoginBanner />
          {isAuthPage
            ? (
              <Box display="flex" flexDirection="column" mt={5} mb={2} gap={3} alignItems="center" justifyContent="center">
                <Link href="/" my={3}><img src={Logo} height={70} alt="Delco Water Company Homepage" /></Link>
                <Typography component="h1" textAlign="center" color={color.grey[900]} fontSize={32}>{title}</Typography>
                <Typography textAlign="center" color={color.grey[700]} fontSize={18} width={isMobile ? '90%' : '60%'}>{desc}</Typography>
              </Box>
            )
            : (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                py={4}
                px={3}
                sx={{ backgroundColor: isMobile ? color.white : color.background[50], borderBottom: isMobile ? `solid 1px ${color.white}` : '0px' }}
              >
                <Box display="flex" alignItems="center">
                  <Link href="/">
                    <img src={Logo} height={isMobile ? 25 : 40} alt="Delco Water Company Homepage" />
                  </Link>
                  {!isMobile && authTitle && (
                    <Typography
                      component="h1"
                      color={color.black}
                      fontSize="32px"
                      fontWeight="600"
                      margin="0 0 0 20px"
                      variant="h1"
                    >
                      {authTitle}
                    </Typography>
                  )}
                </Box>
                <MButton
                  variant="outlined"
                  rounded="true"
                  texttransform="none"
                  onClick={() => {
                    navigate(ROUTES_PATH.SIGNIN)
                  }}
                  sx={{ color: color.primary[700], backgroundColor: color.background[400] }}
                >
                  Back to Login
                </MButton>
              </Box>
            )}
        </PageHeader>
        <Container>
          <MainContent id="main-content" tabIndex={-1}>
            {isMobile && authTitle && (
              <Typography
                component="h1"
                color={color.black}
                fontSize="24px"
                fontWeight="600"
                padding="20px 0 20px 0"
              >
                {authTitle}
              </Typography>
            )}
            <Box width="100%" mb={5}>
              {children}
            </Box>
          </MainContent>
          <Background src={BackgroundImage} alt="" role="presentation" />
          <PageFooter role="contentinfo">
            <Typography component="p" color={color.grey[300]} fontSize="14px">
              © {new Date().getFullYear()}. All rights reserved.
            </Typography>
            <Box
              display="flex"
              textAlign="right"
              gap={1}
              component="nav"
              aria-label="Social media links"
            >
              {FOOTER_SOCIAL_LINKS.map((item, index) => (
                <Box
                  key={index}
                  component="a"
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Follow us on ${item.name}`}
                  sx={{
                    bgcolor: color.background[950],
                    borderRadius: 0.5,
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textDecoration: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <item.icon width={32} height={32} sx={{ color: color.white }} aria-hidden="true" />
                </Box>
              ))}
            </Box>
          </PageFooter>
        </Container>
      </Wrapper >
    </>
  )
}

const Wrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isFullScreen' && prop !== 'isMobile'
})<{ isFullScreen?: boolean, isMobile?: boolean }>(
  ({ isFullScreen, isMobile }) => ({
    background:
      isFullScreen && !isMobile
        ? `linear-gradient(to bottom, ${color.background[50]} 50%, ${color.background[900]} 50%)`
        : `linear-gradient(to bottom, ${color.background[50]} 50vh, ${color.background[900]} 50vh)`,
    display: 'flex',
    flexDirection: 'column',
    height: isFullScreen && !isMobile ? '100vh' : '100%',
    position: 'relative'
  })
)

const PageHeader = styled('header')({
  backgroundColor: color.background[50],
  position: 'relative',
  zIndex: 1
})

const MainContent = styled('main')({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '60vh',
  position: 'relative',
  zIndex: 1
})

const PageFooter = styled('footer')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  position: 'relative',
  paddingTop: '3vh',
  paddingBottom: '3vh',
  zIndex: 1
})

const Background = styled('img')({
  backgroundRepeat: 'no-repeat',
  zIndex: 0,
  left: 0,
  bottom: 0,
  width: '100%',
  height: '25%',
  position: 'absolute'
})
